import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) return type;
  }
  return "";
}

/**
 * Push-to-talk mic → MediaRecorder → POST /agent/transcribe (Gemini).
 * Bypasses Chrome Web Speech cloud STT (common "network" failures).
 */
export function useServerSpeech({ onFinal } = {}) {
  const onFinalRef = useRef(onFinal);
  const mediaRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const listeningRef = useRef(false);

  const [supported] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);
  });
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  const cleanupStream = useCallback(() => {
    try { recorderRef.current?.stop(); } catch (_) { /* noop */ }
    recorderRef.current = null;
    const stream = mediaRef.current;
    mediaRef.current = null;
    stream?.getTracks?.().forEach((t) => {
      try { t.stop(); } catch (_) { /* noop */ }
    });
  }, []);

  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const transcribeBlob = useCallback(async (blob, mimeType) => {
    setTranscribing(true);
    setInterim("Transcribing…");
    setError(null);
    try {
      const form = new FormData();
      const rawType = mimeType || blob.type || "audio/webm";
      const ext = rawType.includes("mp4") || rawType.includes("m4a") ? "m4a" : "webm";
      // File() preserves type; let the browser set multipart boundary (do NOT force Content-Type).
      form.append("audio", new File([blob], `ace-voice.${ext}`, { type: rawType.split(";")[0] }));
      const res = await axios.post(`${API}/agent/transcribe`, form, {
        withCredentials: true,
        timeout: 60000,
      });
      const transcript = (res.data?.transcript || "").trim();
      if (!transcript) {
        setError("Didn't catch that — tap the mic and try again, or switch to Text.");
        return;
      }
      onFinalRef.current?.(transcript);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 503) {
        setError("Voice is offline right now — switch to Text.");
      } else if (detail === "audio_too_large") {
        setError("Recording too long — keep it under ~30 seconds.");
      } else if (typeof detail === "string" && /boundary/i.test(detail)) {
        setError("Voice upload failed — hard-refresh and try again, or switch to Text.");
      } else if (!e?.response) {
        setError("Network error during transcription — check your connection or switch to Text.");
      } else {
        setError("Couldn't transcribe — try again or switch to Text.");
      }
    } finally {
      setTranscribing(false);
      setInterim("");
    }
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch (_) { /* noop */ }
    } else {
      cleanupStream();
      setListening(false);
    }
  }, [cleanupStream]);

  const start = useCallback(async () => {
    if (!supported || listeningRef.current || transcribing) return false;
    setError(null);
    setInterim("");
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      recorderRef.current = rec;

      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onerror = () => {
        setError("Microphone error — try again or switch to Text.");
        cleanupStream();
        setListening(false);
      };
      rec.onstop = async () => {
        setListening(false);
        const type = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        cleanupStream();
        if (blob.size < 256) {
          setError("No speech captured — hold the mic a moment longer.");
          return;
        }
        await transcribeBlob(blob, type);
      };

      rec.start(250);
      setListening(true);
      return true;
    } catch (e) {
      cleanupStream();
      setListening(false);
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Microphone permission denied — allow mic access and try again.");
      } else if (name === "NotFoundError") {
        setError("No microphone found. Plug one in or switch to Text.");
      } else {
        setError("Could not start microphone — tap again or switch to Text.");
      }
      return false;
    }
  }, [supported, transcribing, cleanupStream, transcribeBlob]);

  const toggle = useCallback(() => {
    if (listeningRef.current) stop();
    else start();
  }, [start, stop]);

  return {
    supported,
    listening,
    transcribing,
    interim,
    error,
    start,
    stop,
    toggle,
    clearError: () => setError(null),
  };
}
